import React from "react";
import Box from "@mui/material/Box";
import CaseStudy from "./CaseStudy";
import CaseMasterScore from "./CaseMasterScore";
import ImprovementPointers from "./ImprovementPointers";
import PerformanceRadar from "./PerformanceRadar";
import Reattempt from "./Reattempt";
import FeedbackCard from "components/PerformanceReview4c/FeedbackCard";
import ReviewHeader from "components/PerformanceReview4c/ReviewHeader";

import { Session } from "utils";
import { useParams, useSearchParams } from "react-router-dom";
import { useServices } from 'contexts/SingleSessionProvider';
import { useSnackbar } from 'contexts/SnackbarProvider';
import { getCaseMasterFeedbackByToken, getCaseMasterAttempts, getCaseMasterImprovements } from "services";
import { Break, WhiteBox } from "components/AppShell";
import { Categories, SubCategoryWeightages } from './constants';
import { Skeleton } from "@mui/material";
import { useNavigateWithClient } from "hooks";

const CaseMasterPerformance = () => {
  const { aid } = useParams();
  const [searchParams] = useSearchParams();

  const service = useServices();
  const snackbar = useSnackbar();
  const navigate = useNavigateWithClient();

  const [loading, setLoading] = React.useState(true);
  const [currentAttempt, setCurrentAttempt] = React.useState(null);
  const [caseMaster, setCaseMaster] = React.useState(null);
  const [followups, setFollowups] = React.useState([]);
  const [analysis, setAnalysis] = React.useState([]);
  const [details, setDetails] = React.useState(null);
  const [myScore, setMyScore] = React.useState(1);
  const [categories, setCategories] = React.useState(Categories);
  const [proctorRecords, setProctorRecords] = React.useState([]);
  const [entryPoint, setEntryPoint] = React.useState({});
  const [improvements, setImprovements] = React.useState([]);
  const [loadingImprovements, setLoadingImprovements] = React.useState(false);
  const [isGuestApp, setIsGuestApp] = React.useState(false);

  // Set currentAttempt from URL param
  React.useEffect(() => {
    if (aid) {
      setCurrentAttempt(aid);
    }
  }, [aid]);

  // Fetch attempt details when currentAttempt changes
  React.useEffect(() => {
    if (currentAttempt) {
      getAttempt(currentAttempt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAttempt]);

  // Admin-issued guest links carry a signed token instead of a session, so the
  // attempt is fetched by token rather than by id scoped to the logged-in user.
  React.useEffect(() => {
    const at = searchParams.get('at');
    if (!at) return;

    Session.accessToken = at;
    setIsGuestApp(true);

    getAttemptByToken(at);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transformSummaryToFeedback = React.useCallback((summary) => {
    const feedback = {};

    if (!summary || !summary.categories) {
      console.warn('No summary or categories found in API response');
      return feedback;
    }

    console.log('Summary from API:', summary);

    summary.categories.forEach(category => {
      console.log('Processing category:', category.categoryName, category);

      if (category.parameters && Array.isArray(category.parameters)) {
        category.parameters.forEach(param => {
          if (!param.parameterName) return;

          console.log('Processing parameter:', param.parameterName, 'improvementTips:', param.improvementTips);

          // Map parameter names to uppercase IDs (e.g., "Relevance" -> "RELEVANCE")
          const paramId = param.parameterName.toUpperCase().replace(/\s+/g, '_');

          // Handle improvementTips - can be array or string
          let improvementTips = [];
          if (Array.isArray(param.improvementTips)) {
            improvementTips = param.improvementTips;
          } else if (typeof param.improvementTips === 'string' && param.improvementTips) {
            improvementTips = [param.improvementTips];
          }

          feedback[paramId] = {
            score: param.score !== undefined ? param.score : 0,
            strength: param.strength || '',
            weakness: param.weakness || '',
            improvementTips: improvementTips
          };

          // If this is the Speech category, add features to subcategories
          if (category.categoryName === 'Speech' && category.features && Array.isArray(category.features)) {
            // Filter features for this parameter (FLUENCY or DELIVERY)
            const paramFeatures = category.features.filter(
              feature => feature.category === paramId
            );

            if (paramFeatures.length > 0) {
              feedback[paramId].features = paramFeatures;
            }
          }

          console.log('Created feedback for', paramId, ':', feedback[paramId]);
        });
      }
    });

    console.log('Final transformed feedback:', feedback);
    return feedback;
  }, []);

  // calculating overall score
  const calculateMyScore = React.useCallback((feedback) => {
    try {
      const total = SubCategoryWeightages.reduce((prev, curr) => {
        const { subCategory, weightage } = curr;
        const score = feedback[subCategory]?.score || 0;
        return prev + ((score * weightage) / 100);
      }, 0);

      //Calculates the average and rounding the result to two decimal places
      const avg = Math.round(total * 100) / 100;
      setMyScore(avg < 1 ? 1 : avg);
    } catch (error) {
      console.error('Error calculating score:', error);
      setMyScore(1);
    }
  }, []);

  const curateDetailedFeedback = React.useCallback((feedback) => {
    try {
      const newCategories = Categories.map((category) => {
        const { subCategories } = category;
        const newSubCategories = [];

        const total = subCategories.reduce((prev, curr) => {
          const { weightage, id } = curr;
          const feedbackData = feedback[id] || { score: 0, strength: '', weakness: '', improvementTips: [] };

          newSubCategories.push({ ...curr, ...feedbackData });
          return prev + ((feedbackData.score * weightage) / 100);
        }, 0);

        //Calculates the average and rounding the result to two decimal places
        const avg = Math.round(total * 100) / 100;
        let score = category.id === 'speech' ? avg : ((avg < 1) ? 1 : avg);

        return { ...category, score: score, subCategories: newSubCategories };
      });

      setCategories(newCategories);
    } catch (error) {
      console.error('Error curating feedback:', error);
    }
  }, []);

  // Both entry points hand back the same attempt document, so the mapping onto
  // page state lives here rather than being duplicated per fetch.
  const applyAttempt = React.useCallback((attemptData) => {
    const {
      casemasterProblem,
      casemasterContest,
      finishedAt,
      startedAt,
      audioAnalysis,
      userResponseAndPrompts,
      summary,
      proctorRecords,
    } = attemptData;

    const caseMasterData = Array.isArray(casemasterProblem) ? casemasterProblem[0] : casemasterProblem;

    if (!caseMasterData) {
      throw new Error("Case master problem not found!");
    }

    // Transform new summary structure to old feedback format
    const feedback = transformSummaryToFeedback(summary);

    setDetails({ caseMaster: caseMasterData, finishedAt, startedAt });
    setCaseMaster(caseMasterData);
    setFollowups(userResponseAndPrompts || []);
    setAnalysis(audioAnalysis || []);
    setProctorRecords(proctorRecords || []);

    // Use overallScore from API if available, otherwise calculate
    if (summary?.overallScore !== undefined) {
      setMyScore(summary.overallScore < 1 ? 1 : summary.overallScore);
    } else {
      calculateMyScore(feedback);
    }

    curateDetailedFeedback(feedback);
    setEntryPoint(casemasterContest ? { data: 'contest' } : {});
  }, [transformSummaryToFeedback, calculateMyScore, curateDetailedFeedback]);

  const getAttempt = React.useCallback(async (attemptId) => {
    try {
      setLoading(true);

      const filters = {
        _id: attemptId,
        user: Session.userId,
        includeTotal: true,
        include: ["casemasterProblem"]
      };

      const response = await service.callService(getCaseMasterAttempts, filters);

      const { total, casemasterAttempts } = response || {};

      if (!total || !casemasterAttempts || casemasterAttempts.length === 0) {
        throw new Error("Details not found!");
      }

      applyAttempt(casemasterAttempts[0]);
    } catch (error) {
      console.error('Error in getAttempt:', error);
      snackbar.error("Uh Oh! Unable to get details. Please try again.", false);
      navigate("/personalized-interview?type=case-master");
    } finally {
      setLoading(false);
    }
  }, [service, snackbar, navigate, applyAttempt]);

  // Guest links have no session to navigate back to, so a failure here reports
  // the error and leaves the page empty instead of redirecting.
  const getAttemptByToken = React.useCallback(async (accessToken) => {
    try {
      setLoading(true);

      const attemptData = await service.callService(getCaseMasterFeedbackByToken, accessToken);

      if (!attemptData) throw new Error("Details not found!");

      // Deliberately not setting currentAttempt: that drives the session-scoped
      // getAttempt effect, which a guest link cannot satisfy.
      applyAttempt(attemptData);
    } catch (error) {
      console.error('Error in getAttemptByToken:', error);
      snackbar.error("Uh Oh! Unable to get details. Please try again.", false);
    } finally {
      setLoading(false);
    }
  }, [service, snackbar, applyAttempt]);

  const getImprovements = React.useCallback(async (attemptId) => {
    try {
      setLoadingImprovements(true);

      const pointers = await service.callService(getCaseMasterImprovements, attemptId);

      setImprovements(pointers || []);
    } catch (error) {
      console.error('Error fetching improvements:', error);
      setImprovements([]);
    } finally {
      setLoadingImprovements(false);
    }
  }, [service]);

  // useServices() hands back a new object every render, so getImprovements
  // cannot be a dependency here - it would re-run this effect on every render.
  React.useEffect(() => {
    if (currentAttempt && !isGuestApp) getImprovements(currentAttempt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAttempt, isGuestApp]);

  // Handle attempt selection - navigate to new URL
  const handleAttemptChange = React.useCallback((attemptId) => {
    navigate(`/performance/case-master/${attemptId}`);
  }, [navigate]);

  return (
    <Box width='100%' display='flex' flexDirection='column' gap='16px'>

      <ReviewHeader
        currentAttempt={currentAttempt}
        setCurrentAttempt={handleAttemptChange}
        details={details}
        isGuestApp={isGuestApp}
        type="case-master"
      />

      {
        loading ?
          <Skeleton variant='rectangular' width={'100%'} height={300} /> :
          <>
            <WhiteBox>
              <CaseMasterScore 
                score={myScore} 
                proctorRecords={proctorRecords}
                maxScore={10}
                scoreLabel="My score:"
                tooltipTitle="Score based on case interview performance across multiple dimensions."
                showDescription={false}
              />
            </WhiteBox>

            <WhiteBox>
              <PerformanceRadar categories={categories} />
            </WhiteBox>

            <CaseStudy
              caseMaster={caseMaster}
              followUps={followups}
              analysis={analysis}
              startedAt={details?.startedAt}
              finishedAt={details?.finishedAt}
            />

            <WhiteBox>
              <FeedbackCard categories={categories} maxScore={100} isCaseStudy={true} />
            </WhiteBox>

            {(loadingImprovements || improvements.length > 0) && (
              <WhiteBox>
                <ImprovementPointers
                  pointers={improvements}
                  loading={loadingImprovements}
                />
              </WhiteBox>
            )}
          </>
      }

      {
        entryPoint?.data !== 'contest' && !isGuestApp && <Reattempt caseMaster={caseMaster} />
      }
    </Box>

  )
}

export default CaseMasterPerformance
